import {
  EBindingReason,
  EInactivationChoiceOption,
} from '@settings/data-access';

/**
 * Inactivation attempt — the router of "Ativar cliente" (personservice
 * `POST /Person/{id}/InactivationAttempts`, feature 003). Mirrors
 * `InactivationAttemptViewModel.cs` 1:1; enums by number.
 *
 * `EBindingReason` and `EInactivationChoiceOption` live in
 * `@settings/data-access` (`audit-trail.model.ts`): the audit trail writes
 * them by name inside `details`, and that lib cannot import this one.
 */

/** `SubscriptionPaymentKind`: how the plan is paid. */
export enum ESubscriptionPaymentKind {
  /** À vista: a single installment, or a cycle in days. */
  Prepaid = 1,
  /** Monthly, more than one installment. */
  Installments = 2,
  /** `IsRecurring` with a card. */
  Recurring = 3,
}

/** `BindingPauseBalanceViewModel`: pause balance on the current renewal. `null` limit = unlimited. */
export type TBindingPauseBalance = {
  MaxPauses: number | null;
  UsedPauses: number;
  MaxPausedDays: number | null;
  UsedPausedDays: number;
  RemainingDays: number | null;
};

/** `BindingSubscriptionViewModel`: a subscription that holds the person file active. */
export type TBindingSubscription = {
  SubscriptionId: string;
  Name: string | null;
  PlanName: string | null;
  CycleName: string | null;
  /** The plan and cycle of the subscription: "Nova ação" of the router opens with them. */
  PlanId: string | null;
  CycleId: string | null;
  IsDependent: boolean;
  HolderPersonId: string | null;
  /** The holder's name when the person is a dependent on the plan (same account only); `null` otherwise. */
  HolderName: string | null;
  BindingReasons: EBindingReason[];
  PaymentKind: ESubscriptionPaymentKind | null;
  TotalAmountCents: number | null;
  StartOnUtc: string | null;
  EndOnUtc: string | null;
  ElapsedMonths: number | null;
  TotalMonths: number | null;
  NextChargeOnUtc: string | null;
  /**
   * Last 4 digits of the card of a recurring subscription. `null` for the other
   * payment kinds, and when the payment gateway does not find the card or does
   * not answer in time: the router then says just "cartão".
   */
  CardLast4: string | null;
  PausedUntilOnUtc: string | null;
  RenewalScheduledOnUtc: string | null;
  PauseBalance: TBindingPauseBalance;
};

/**
 * `InactivationAttemptViewModel`. `Allowed: true` → the usual flow goes on.
 * `Allowed: false` → modal 1 (one subscription) or 3 (several), and the
 * attempt is on the timeline as `InactivationBlocked`.
 */
export type TInactivationAttempt = {
  Allowed: boolean;
  /** The blocked attempt the router's choice binds to. `null` when allowed. */
  AttemptId: string | null;
  Subscriptions: TBindingSubscription[];
};

/** `InactivationChoiceViewModel`: body of `…/InactivationAttempts/{attemptId}/Choice`. */
export type TInactivationChoiceRequest = {
  Option: EInactivationChoiceOption;
};

/**
 * The router option handed to the profile's subscriptions tab, which owns the
 * pause, switch and close flows. Front-end state only (never sent): the tab
 * consumes it once.
 * - `pause`: new pause form; `pauses`: the plan's current pauses;
 * - `switch`: switch modal; `close`: close modal (then "Plano encerrado").
 */
export type TInactivationHandoff = {
  personId: string;
  subscriptionId: string;
  action: 'pause' | 'pauses' | 'switch' | 'close';
};
